import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  SearchInput,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { errorMessage } from '@/lib/api';
import { listProposals, type ProposalSummary } from '@/proposals/lib/api';
import { formatMoney, formatShortDate } from '@/proposals/lib/money';
import { NewProposalDialog } from '@/proposals/NewProposalDialog';

/** Sortable columns. */
const PROPOSAL_SORT_FIELDS = {
  name: 'name',
  client: 'client',
  stage: 'stage',
  net: 'net',
  updated: 'updatedAt',
} as const;
const PROPOSAL_SORT_KEYS = Object.values(PROPOSAL_SORT_FIELDS);
const DEFAULT_SORT = { key: PROPOSAL_SORT_FIELDS.updated, direction: 'desc' as const };

/** A list row with the nested fields flattened so `sortRows` can order by them. */
interface ProposalRow {
  readonly id: string;
  readonly name: string;
  readonly client: string;
  readonly stage: string;
  /** The single option's net, or (with several) the lowest — for sorting/display. */
  readonly net: number;
  readonly optionCount: number;
  readonly hasPricing: boolean;
  readonly updatedAt: string;
}

/** The row fields the search box matches. */
const PROPOSAL_SEARCH_FIELDS: ReadonlyArray<keyof ProposalRow> = ['name', 'client', 'stage'];

function toRow(proposal: ProposalSummary): ProposalRow {
  const nets = proposal.totals.options.map((o) => o.net);
  const hasPricing = proposal.pricing.options.some((o) => o.programs.length > 0);
  return {
    id: proposal.id,
    name: proposal.name,
    client: proposal.opportunity?.accountName ?? '',
    stage: proposal.opportunity?.stageName ?? '',
    net: nets.length > 0 ? Math.min(...nets) : 0,
    optionCount: proposal.totals.options.length,
    hasPricing,
    updatedAt: proposal.updatedAt ?? '',
  };
}

/** The row's net: one option's price, "from" the lowest of several, or not priced yet. */
function netLabel(row: ProposalRow): React.ReactNode {
  if (!row.hasPricing) return <span className="text-portal-muted">Not priced</span>;
  return (
    <>
      {row.optionCount > 1 ? <span className="text-portal-muted">from </span> : null}
      {formatMoney(row.net)}
    </>
  );
}

/**
 * The proposal generator's list page: the proposals you can see (yours, or all
 * of them for admins), newest first, and a way to start one from an
 * opportunity. Gated by the `generate_proposals` permission at the route.
 *
 * @returns The proposals list.
 */
export function ProposalsPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sort, onSort } = useTableSort(PROPOSAL_SORT_KEYS, DEFAULT_SORT);
  const [creating, setCreating] = useState(false);
  const { search, setSearch } = useUrlSearch();

  const proposals = useQuery({
    queryKey: ['proposals'],
    queryFn: () => listProposals(),
  });

  const rows = useMemo(
    () => sortRows(filterRows((proposals.data ?? []).map(toRow), search, PROPOSAL_SEARCH_FIELDS), sort),
    [proposals.data, search, sort],
  );

  const newButton = (
    <Button variant="primary" onClick={() => setCreating(true)}>
      New proposal
    </Button>
  );

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        title="Proposals"
        subtitle="Price a client proposal from one of your opportunities and build it in the block editor."
        action={newButton}
      />

      <NewProposalDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          void queryClient.invalidateQueries({ queryKey: ['proposals'] });
          navigate(`/proposals/${id}`);
        }}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search proposals"
          placeholder="Name, client or stage"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
      </div>

      {proposals.isPending ? (
        <SkeletonTable />
      ) : proposals.isError ? (
        <ErrorNotice title="Proposals unavailable" message={errorMessage(proposals.error)} />
      ) : rows.length === 0 && proposals.data.length > 0 ? (
        <Panel>
          <EmptyState title="No proposals match" message="Try a different search." />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            title="No proposals yet"
            message="Start a client proposal from one of your opportunities, price it, then edit it in the block editor."
            action={newButton}
          />
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Name', sortKey: PROPOSAL_SORT_FIELDS.name },
            { label: 'Client', sortKey: PROPOSAL_SORT_FIELDS.client },
            { label: 'Stage', sortKey: PROPOSAL_SORT_FIELDS.stage },
            { label: 'Net', sortKey: PROPOSAL_SORT_FIELDS.net },
            { label: 'Updated', sortKey: PROPOSAL_SORT_FIELDS.updated },
          ]}
          rightAlignedHeaders={[3]}
          sort={sort}
          onSort={onSort}
          cards={rows.map((row) => ({
            key: row.id,
            title: row.name,
            ...(row.client ? { subtitle: row.client } : {}),
            fields: [
              { label: 'Net', value: netLabel(row) },
              { label: 'Stage', value: row.stage || '—' },
              { label: 'Updated', value: formatShortDate(row.updatedAt) },
            ],
            onOpen: () => navigate(`/proposals/${row.id}`),
          }))}
        >
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <Link to={`/proposals/${row.id}`} className="font-bold text-primary hover:underline">
                  {row.name}
                </Link>
              </td>
              <td className="p-3 text-portal-small text-portal-muted">{row.client || '—'}</td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{row.stage || '—'}</td>
              <td className="p-3 text-portal-small text-right whitespace-nowrap">
                {netLabel(row)}
              </td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">
                {formatShortDate(row.updatedAt)}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </section>
  );
}
